import { useEffect, useMemo, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Dialog({
  isOpen,
  onClose,
  labelledBy,
  describedBy,
  children,
  panelClassName = "w-full max-w-md rounded-2xl bg-white p-6 shadow-xl",
  closeOnBackdrop = true,
}) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const previousActiveRef = useRef(null);
  const titleId = useMemo(
    () => labelledBy || `dialog-title-${Math.random().toString(36).slice(2, 10)}`,
    [labelledBy]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    previousActiveRef.current = document.activeElement;
    const bodyOriginalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const panel = panelRef.current;
    const autofocusNode = panel?.querySelector("[data-autofocus='true']");
    const focusables = panel?.querySelectorAll(FOCUSABLE_SELECTOR);
    if (autofocusNode instanceof HTMLElement) {
      autofocusNode.focus();
    } else if (focusables?.length) {
      focusables[0].focus();
    } else {
      panel?.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== "Tab") return;

      const nodes = panel?.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!nodes || nodes.length === 0) {
        event.preventDefault();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = bodyOriginalOverflow;
      if (previousActiveRef.current instanceof HTMLElement) {
        previousActiveRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 backdrop-blur-[1px]"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === overlayRef.current) onClose?.();
      }}
      aria-hidden="true"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={panelClassName}
      >
        {typeof children === "function" ? children({ titleId }) : children}
      </div>
    </div>
  );
}

export default Dialog;
